import { View } from 'react-native'

export const TASK_EVENT_HEIGHT = 90

export const TASK_EVENT_SUMMARY_HEIGHT = 120

export const TASK_EVENT_TOP_MARGIN = 5

import { Task, TaskEvents, TaskOccurrence } from '@nextforce/api'
import { Divider, Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useCallback, useMemo, useState } from 'react'
import { Card } from 'shared/components/card'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { getTaskStatus, TaskStatus, useTaskStatusColor } from 'tasks/utils'

interface TaskEventViewProps {
  event: TaskEvents
  onPressTask: (taskEvent: Task) => void
}

export default function TaskEventListView({
  event,
  onPressTask,
}: TaskEventViewProps) {
  const styles = useStyles()
  if (!event.tasks.length) {
    return null
  }

  return (
    <View style={styles.taskEventListViewContainer}>
      {getTaskEventViews(event.tasks, onPressTask)}
    </View>
  )
}

function getTaskEventViews(tasks: Task[], onPressTask: (task: Task) => void) {
  // Define status priority order
  const statusPriority: Record<TaskStatus, number> = {
    done: 1, // First priority
    due: 2,
    open: 3,
    late: 4,
  }

  // Sort tasks based on their status priority
  const sortedTasks = [...tasks].sort((taskA, taskB) => {
    const statusA = getTaskStatus(taskA)
    const statusB = getTaskStatus(taskB)
    return statusPriority[statusA] - statusPriority[statusB]
  })

  return sortedTasks.flatMap(task =>
    task.occurrences
      .filter(occurrence => !occurrence.deleted)
      .map((occurrence, occIndex) => (
        <TaskEventCard
          task={task}
          occurrence={occurrence}
          key={`${task.storeId}-${occurrence.taskId}-${occIndex}`}
          onPressTask={onPressTask}
        />
      ))
  )
}

interface TaskEventSummaryViewProps {
  event: TaskEvents
  onPressTask: (task: Task) => void
}

export function TaskEventSummaryView({
  event,
  onPressTask,
}: TaskEventSummaryViewProps) {
  const [expanded, setExpanded] = useState(false)
  const { theme } = useTheme()
  const styles = useStyles()

  const { doneCount, dueCount, openCount, lateCount } = useMemo(() => {
    let done = 0
    let due = 0
    let open = 0
    let late = 0

    event.tasks.forEach(task => {
      const taskStatus = getTaskStatus(task)
      switch (taskStatus) {
        case 'done':
          done++
          break
        case 'due':
          due++
          break
        case 'open':
          open++
          break
        case 'late':
          late++
          break
      }
    })

    return { doneCount: done, dueCount: due, openCount: open, lateCount: late }
  }, [event])

  const totalCount = doneCount + dueCount + openCount + lateCount
  const toggleExpanded = useCallback(() => setExpanded(!expanded), [expanded])
  if (totalCount === 0) {
    return null
  }

  return (
    <View>
      <PressableOpacity onPress={toggleExpanded}>
        <Card style={styles.summaryCard}>
          <View style={styles.taskSummaryLabelContainer}>
            <Text numberOfLines={1}>
              {t('tasksCount', { count: totalCount }).toUpperCase()}
            </Text>
          </View>
          <Divider />
          <View style={styles.inner}>
            <View style={styles.taskSummaryStatusItemsContainer}>
              <View style={styles.taskSummaryItem}>
                <Text style={styles.taskSummaryItemText} numberOfLines={1}>
                  {doneCount}
                </Text>
                <Text>{t('done').toUpperCase()}</Text>
              </View>
              <View style={styles.taskSummaryItem}>
                <Text style={styles.taskSummaryItemText} numberOfLines={1}>
                  {dueCount}
                </Text>
                <Text>{t('due').toUpperCase()}</Text>
              </View>
              <View style={styles.taskSummaryItem}>
                <Text style={styles.taskSummaryItemText} numberOfLines={1}>
                  {openCount}
                </Text>
                <Text>{t('open').toUpperCase()}</Text>
              </View>
              <View style={styles.taskSummaryItem}>
                <Text style={styles.taskSummaryItemText} numberOfLines={1}>
                  {lateCount}
                </Text>
                <Text>{t('late').toUpperCase()}</Text>
              </View>
            </View>
            <View style={styles.caretContainer}>
              <View style={styles.caret}>
                <Icon
                  name={expanded ? 'angle-down' : 'angle-right'}
                  type="font-awesome-5"
                  solid
                  color={theme.colors?.grey2}
                  size={20}
                />
              </View>
            </View>
          </View>
        </Card>
      </PressableOpacity>
      {expanded ? (
        <TaskEventListView event={event} onPressTask={onPressTask} />
      ) : null}
    </View>
  )
}

interface TaskEventCardProps {
  task: Task
  occurrence: TaskOccurrence
  onPressTask: (task: Task) => void
}

export function TaskEventCard({
  task,
  occurrence,
  onPressTask,
}: TaskEventCardProps) {
  const styles = useStyles()

  const taskStatus = getTaskStatus(task)
  const taskStatusColor = useTaskStatusColor(taskStatus)

  return (
    <PressableOpacity onPress={() => onPressTask(task)}>
      <Card style={styles.card} borderColor={taskStatusColor} rightBorder>
        <View style={styles.container}>
          <View style={styles.summaryTextContainer}>
            <Text style={styles.summary} numberOfLines={3}>
              {occurrence.summary}
            </Text>
          </View>
          <View style={styles.userAvatarContainer}>
            <UserAvatar
              user={task.assignedTo}
              showAvatarIfNoPhone
              size={'medium'}
            />
          </View>
          <View style={styles.userTextContainer}>
            <Text>
              {task.assignedTo.firstName} {task.assignedTo.lastName}
            </Text>
            {task.assignedTo.jobTitle ? (
              <Text>{task.assignedTo.jobTitle}</Text>
            ) : null}
          </View>
          <View style={styles.statusTextContainer}>
            <Text style={styles.statusText}>{taskStatus.toUpperCase()}</Text>
          </View>
        </View>
      </Card>
    </PressableOpacity>
  )
}

const useStyles = makeStyles(theme => ({
  card: {
    marginTop: TASK_EVENT_TOP_MARGIN,
    height: TASK_EVENT_HEIGHT,
    paddingLeft: 5,
  },
  summaryCard: {
    marginTop: TASK_EVENT_TOP_MARGIN,
    height: TASK_EVENT_SUMMARY_HEIGHT,
  },
  container: {
    flex: 1,
    alignItems: 'stretch',
    justifyContent: 'space-around',
    flexDirection: 'row',
    marginLeft: 10,
  },
  summary: {
    fontSize: 13,
  },
  statusTextContainer: {
    alignItems: 'flex-end',
  },
  statusText: {
    fontWeight: 'bold',
    fontSize: 12,
  },
  caretContainer: {
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  caret: {
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    height: 21,
    minWidth: 21,
    marginLeft: 5,
  },
  inner: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 2,
  },
  taskSummaryStatusItemsContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  taskSummaryItem: {
    flex: 1,
    alignItems: 'center',
  },
  taskSummaryItemText: {
    marginBottom: 5,
    fontSize: 18,
  },
  taskEventListViewContainer: { flex: 1 },
  taskSummaryLabelContainer: { marginBottom: 2, alignItems: 'flex-end' },
  summaryTextContainer: {
    width: 110,
    marginRight: 5,
    justifyContent: 'center',
  },
  userAvatarContainer: { justifyContent: 'center' },
  userTextContainer: {
    flex: 1,
    marginLeft: 10,
    justifyContent: 'center',
  },
}))
